// Agents.tsx — admin page: keep records of your agents (brokers, channel
// partners, field agents). Agents are records, not logins.
//
// Route (sidebar entry is hidden for staff in routeConfig.ts):
//   <Route path="/agents" element={<Agents />} />
//
// ROLES
//   admin → everything on this page
//   staff → never sees the menu entry; opening the URL shows an "admins only"
//           panel and NOTHING is fetched (the API would refuse writes anyway)
//   role not loaded yet → spinner, nothing fetched

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { ArrowLeft, ArrowLeftRight, Briefcase, Eye, Lock, MessageCircle, Pencil, Phone, Plus, Search, Trash2, UserPlus } from 'lucide-react';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import AgentFormModal from './parts/AgentFormModal';
import AgentDeleteModal from './parts/AgentDeleteModal';
import AgentViewModal from './parts/AgentViewModal';
import AgentToLeadModal from './parts/AgentToLeadModal';
import AgentsListSkeleton from '../../components/skeletons/AgentsListSkeleton';
import { Notice, SHELL, INNER } from '../Proposals/parts/ProposalDialog';
import { apiError, fmtDate, useProposalRole } from '../Proposals/parts/proposalTypes';
import { Agent, agentApi, fmtPhone, waLink } from './parts/agentTypes';

type StatusFilter = 'all' | 'active' | 'inactive';

const TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '9px 12px',
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: C.sub,
  background: G.shellBg,
  borderBottom: `1px solid ${G.line}`,
  whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = { padding: '10px 12px', fontSize: '0.75rem', color: '#1f2937', borderBottom: `1px solid ${G.softer}`, verticalAlign: 'middle' };

const iconBtn = (danger = false): React.CSSProperties => ({
  width: '28px',
  height: '28px',
  borderRadius: '7px',
  border: `1px solid ${danger ? 'rgba(239,68,68,0.3)' : G.line}`,
  background: danger ? 'rgba(239,68,68,0.06)' : C.white,
  color: danger ? '#c62828' : C.teal,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Switch: React.FC<{ on: boolean; busy: boolean; onClick: () => void; label: string }> = ({ on, busy, onClick, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={busy}
    onClick={onClick}
    title={on ? 'Active — click to mark inactive' : 'Inactive — click to mark active'}
    style={{ width: '34px', height: '20px', borderRadius: '20px', border: 'none', padding: '2px', background: on ? C.teal : '#d1d5db', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, transition: 'background 0.2s', display: 'flex', alignItems: 'center', justifyContent: on ? 'flex-end' : 'flex-start' }}
  >
    <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
  </button>
);

const initials = (name: string) => {
  const p = name.trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[p.length - 1][0] : name.slice(0, 2)).toUpperCase();
};

const Agents: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef<Toast>(null);
  const { isAdmin, roleKnown, profileError } = useProposalRole();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');

  const [formModal, setFormModal] = useState<{ open: boolean; item: Agent | null }>({ open: false, item: null });
  const [viewing, setViewing] = useState<Agent | null>(null);
  const [movingBack, setMovingBack] = useState<Agent | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await agentApi.list();
      setAgents(res.data ?? []);
      setError(null);
    } catch (e) {
      if (!silent) setError(apiError(e, 'Failed to load agents'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  // arrived from a lead's "TO AGENT" button
  useEffect(() => {
    const name = (location.state as { transferred?: string } | null)?.transferred;
    if (!name) return;
    toast.current?.show({ severity: 'success', summary: 'Moved to Agents', detail: name, life: 3000 });
    navigate(location.pathname, { replace: true, state: null }); // don't repeat on refresh
  }, [location, navigate]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return agents.filter((a) => {
      if (status === 'active' && !a.is_active) return false;
      if (status === 'inactive' && a.is_active) return false;
      if (!q) return true;
      return [a.name, a.phone, a.email, a.company, a.profession, a.city].some((v) => (v ?? '').toLowerCase().includes(q));
    });
  }, [agents, query, status]);

  const activeCount = agents.filter((a) => a.is_active).length;

  const handleToggle = async (a: Agent) => {
    setBusyIds((s) => new Set(s).add(a.id));
    try {
      const res = await agentApi.update(a.id, { is_active: !a.is_active });
      setAgents((list) => list.map((x) => (x.id === a.id ? res.data : x)));
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Could not change status', detail: apiError(e, 'Try again.'), life: 3500 });
    } finally {
      setBusyIds((s) => {
        const n = new Set(s);
        n.delete(a.id);
        return n;
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await agentApi.remove(deleteTarget.id, !!deleteTarget.has_lead_history);
      toast.current?.show({ severity: 'success', summary: 'Agent deleted', detail: deleteTarget.name, life: 2500 });
      setDeleteTarget(null);
      load(true);
    } catch (e) {
      setDeleteError(apiError(e, 'Could not delete the agent. Try again.'));
    } finally {
      setDeleting(false);
    }
  };

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!roleKnown) {
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '240px', padding: '20px' }}>
          {profileError ? (
            <Message severity="error" text={`Could not check your role: ${profileError}`} style={{ fontSize: '0.83rem' }} />
          ) : (
            <ProgressSpinner style={{ width: '34px', height: '34px' }} strokeWidth="3" animationDuration=".8s" />
          )}
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, textAlign: 'center', padding: '44px 20px' }}>
          <div style={{ width: 44, height: 44, borderRadius: '12px', background: G.shellBg, border: `1px solid ${G.line}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
            <Lock size={20} color={C.teal} />
          </div>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: C.teal, marginBottom: '5px' }}>Only admins can manage agents</div>
          <div style={{ fontSize: '0.78rem', color: C.sub, marginBottom: '16px' }}>Ask an admin if an agent’s details need changing.</div>
          <GlobalButton label="BACK TO LEADS" icon={ArrowLeft} onClick={() => navigate('/leads')} />
        </div>
      </div>
    );
  }

  // ── Admin view ─────────────────────────────────────────────────────────────
  const chip = (key: StatusFilter, label: string): React.ReactNode => {
    const on = status === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => setStatus(key)}
        style={{ padding: '6px 11px', borderRadius: '8px', border: `1px solid ${on ? C.teal : G.line}`, background: on ? C.teal : C.white, color: on ? C.white : C.sub, fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ margin: '0 auto' }}>
      <style>{`.ag-row:hover td { background: #fbfcfc; }`}</style>
      <Toast ref={toast} />

      <AgentFormModal
        visible={formModal.open}
        agent={formModal.item}
        onHide={() => setFormModal({ open: false, item: null })}
        onSaved={(a) => {
          toast.current?.show({ severity: 'success', summary: formModal.item ? 'Agent updated' : 'Agent added', detail: a.name, life: 2500 });
          load(true);
        }}
      />

      <AgentViewModal
        visible={!!viewing}
        agent={viewing}
        onHide={() => setViewing(null)}
        onEdit={(a) => {
          setViewing(null);
          setFormModal({ open: true, item: a });
        }}
        onViewLeads={(a) => navigate(`/leads?agent_id=${a.id}`)}
        onMoveToLeads={(a) => {
          setViewing(null);
          setMovingBack(a);
        }}
      />

      <AgentToLeadModal
        visible={!!movingBack}
        agent={movingBack}
        onHide={() => setMovingBack(null)}
        onMoved={(leadId) => {
          setMovingBack(null);
          navigate(`/leads/${leadId}/details`);
        }}
      />

      <AgentDeleteModal
        visible={!!deleteTarget}
        name={deleteTarget?.name}
        hasLeadHistory={!!deleteTarget?.has_lead_history}
        onMoveInstead={() => {
          const a = deleteTarget;
          setDeleteTarget(null);
          setDeleteError(null);
          setMovingBack(a);
        }}
        loading={deleting}
        error={deleteError}
        onCancel={() => {
          if (!deleting) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={confirmDelete}
      />

      {/* header */}
      <div style={{ ...SHELL, marginBottom: '11px' }}>
        <div style={{ ...INNER, padding: '13.8px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '11px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
            <div style={{ width: 38, height: 38, borderRadius: '10px', background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`, color: C.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Briefcase size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: '1.01rem', fontWeight: 700, color: C.teal }}>Agents</h1>
              <div style={{ fontSize: '0.71rem', color: C.sub }}>
                {agents.length} agent{agents.length === 1 ? '' : 's'} · {activeCount} active · {agents.reduce((n, a) => n + a.lead_count, 0)} leads brought in
              </div>
            </div>
          </div>
          <GlobalButton label="NEW AGENT" size="small" icon={Plus} onClick={() => setFormModal({ open: true, item: null })} />
        </div>
      </div>

      {loading ? (
        <AgentsListSkeleton />
      ) : error ? (
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '16px' }}>
            <Notice tone="error">{error}</Notice>
            <div style={{ marginTop: '10px' }}>
              <GlobalButton label="TRY AGAIN" variant="outline" size="small" onClick={() => load()} />
            </div>
          </div>
        </div>
      ) : (
        <div style={SHELL}>
          <div style={{ ...INNER, overflow: 'hidden' }}>
            {/* toolbar */}
            <div style={{ padding: '11px 13px', display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', borderBottom: `1px solid ${G.line}` }}>
              <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '300px' }}>
                <Search size={13} color={C.muted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, phone, company, city…"
                  style={{ width: '100%', boxSizing: 'border-box', height: '34px', padding: '0 10px 0 29px', borderRadius: '8px', border: `1px solid ${G.line}`, fontSize: '0.74rem', color: '#1f2937', outline: 'none', background: C.white }}
                />
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {chip('all', 'All')}
                {chip('active', 'Active')}
                {chip('inactive', 'Inactive')}
              </div>
            </div>

            {agents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ width: 44, height: 44, borderRadius: '12px', background: G.shellBg, border: `1px solid ${G.line}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                  <UserPlus size={20} color={C.teal} />
                </div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: C.teal, marginBottom: '4px' }}>No agents yet</div>
                <div style={{ fontSize: '0.76rem', color: C.sub, marginBottom: '14px' }}>Add the people who bring you leads, and keep their details in one place.</div>
                <GlobalButton label="ADD YOUR FIRST AGENT" size="small" icon={UserPlus} onClick={() => setFormModal({ open: true, item: null })} />
              </div>
            ) : visible.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 20px', fontSize: '0.78rem', color: C.sub }}>No agents match your search.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '880px' }}>
                  <thead>
                    <tr>
                      <th style={TH}>Agent</th>
                      <th style={TH}>Phone</th>
                      <th style={TH}>City</th>
                      <th style={TH}>Leads</th>
                      <th style={TH}>Commission</th>
                      <th style={TH}>Active</th>
                      <th style={TH}>Added</th>
                      <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((a) => (
                      <tr key={a.id} className="ag-row" style={{ opacity: a.is_active ? 1 : 0.55 }}>
                        <td style={TD}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: C.limeDim, border: `1px solid ${C.limeBorder}`, color: '#4a5e00', fontSize: '0.66rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(a.name)}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '230px' }} title={a.notes || undefined}>{a.name}</div>
                              <div style={{ fontSize: '0.66rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '230px' }}>{[a.profession, a.company, a.email].filter(Boolean).join(' · ') || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td style={TD}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                            <a href={`tel:${a.phone}`} title="Call" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#1f2937', textDecoration: 'none', fontWeight: 600 }}>
                              <Phone size={12} color={C.tealMid} /> {fmtPhone(a.phone)}
                            </a>
                            <a href={waLink(a.phone)} target="_blank" rel="noopener noreferrer" title="Open WhatsApp" style={{ display: 'inline-flex', color: '#128c4a' }}>
                              <MessageCircle size={14} />
                            </a>
                          </span>
                        </td>
                        <td style={TD}>{a.city || <span style={{ color: C.muted }}>—</span>}</td>
                        <td style={TD}>
                          {a.lead_count > 0 ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/leads?agent_id=${a.id}`)}
                              title="See this agent's leads"
                              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: C.teal }}
                            >
                              <span style={{ fontWeight: 700, textDecoration: 'underline' }}>{a.lead_count}</span>
                              <span style={{ display: 'block', fontSize: '0.62rem', color: C.muted }}>{a.converted_count} converted</span>
                            </button>
                          ) : (
                            <span style={{ color: C.muted }}>0</span>
                          )}
                        </td>
                        <td style={TD}>{a.commission != null ? <span style={{ fontWeight: 700 }}>{a.commission}%</span> : <span style={{ color: C.muted }}>—</span>}</td>
                        <td style={TD}>
                          <Switch on={a.is_active} busy={busyIds.has(a.id)} onClick={() => handleToggle(a)} label={`${a.name} active`} />
                        </td>
                        <td style={{ ...TD, color: C.sub, whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
                        <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', gap: '6px' }}>
                            <button type="button" title="View agent" onClick={() => setViewing(a)} style={iconBtn()}>
                              <Eye size={13} />
                            </button>
                            <button
                              type="button"
                              title={a.lead_count > 0 ? 'This agent has leads on record — deal with those first' : 'Move to Leads'}
                              onClick={() => setMovingBack(a)}
                              style={{ ...iconBtn(), opacity: a.lead_count > 0 ? 0.45 : 1 }}
                            >
                              <ArrowLeftRight size={13} />
                            </button>
                            <button type="button" title="Edit agent" onClick={() => setFormModal({ open: true, item: a })} style={iconBtn()}>
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              title="Delete agent"
                              onClick={() => {
                                setDeleteError(null);
                                setDeleteTarget(a);
                              }}
                              style={iconBtn(true)}
                            >
                              <Trash2 size={13} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;